import React, { FC, useCallback, useState } from 'react';
import { useQuery } from '@apollo/client';
import { GUIDE_CMS_INFO } from './content.graphql';
import { Editor } from '@tiptap/react';
import { GuideCmsInfo } from './types';
import { TiptapCollabProvider } from '@tiptap-pro/provider';
import { GET_GUIDE_AND_LINKS } from '../guide.graphql';
import { ALL_STANDARDS } from '../../standards/standards.graphql';
import { useModal } from '../../dialogs/useModal';
import { InternalLinksDialog, InternalLinksDialogProps, InternalLinksDialogResult } from './dialogs/InternalLinksDialog';
import { GuideLinkDialog, GuideLinkDialogProps, GuideLinkDialogResult } from './dialogs/GuideLinkDialog';
import { CitationLinkDialog, CitationLinkDialogProps, CitationLinkDialogResult } from './dialogs/CitationLinkDialog';
import { MigrateCitationsDialog, MigrateCitationsDialogProps, MigrateCitationsDialogResult } from './dialogs/MigrateCitationsDialog';
import { StandardLinkDialog, StandardLinkDialogProps, StandardLinkDialogResult } from './dialogs/StandardLinkDialog';
import { TiptapDocumentItemFragment, GetGuideCmsInfoQuery } from '../../__generated__/graphql';
import { useDictionarySync } from './sidebar/dictionary/useDictionarySync';
import { Unpacked } from '../../graphQLTypes/types';

export interface SnapshotVersion {
  version: number;
  name?: string;
  date: number;
}

export interface SnapshotState {
  versions: SnapshotVersion[];
  currentVersion: number;
  latestVersion: number;
}

export type Release = Unpacked<NonNullable<GetGuideCmsInfoQuery['guide']>['releases']>;

export type DictionaryRequest = { termId: string; search?: never } | { search: string; termId?: never };

type SidebarType = 'toc' | 'comments' | 'help' | 'references' | 'figures' | 'dictionary';

export interface GuideCmsContext {
  isLoading: boolean;
  isArchive: boolean;
  archivedVersion?: string;
  guideId: string;
  isValidSchema: boolean;
  setIsValidSchema: (isValidSchema: boolean) => void;
  guide: GuideCmsInfo;
  release: Release;
  tiptapDocument: TiptapDocumentItemFragment | null;
  setTiptapDocument: (tiptapDocument: TiptapDocumentItemFragment) => void;
  editor: Editor | null;
  provider?: TiptapCollabProvider;
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  sidebarType: SidebarType;
  setSidebarType: (sidebarType: SidebarType) => void;
  setEditor: (editor: Editor) => void;
  setProvider: (provider: TiptapCollabProvider) => void;
  forceRefreshEditors: () => void;
  getGuideTitle: (guideId: string) => string | null;
  getStandardTitle: (standardId: string) => string | null;
  getCitationText: (citationId: string) => string | null;
  isReferenceCandidateHighlightEnabled: boolean;
  setIsReferenceCandidateHighlightEnabled: (enabled: boolean) => void;
  isRejectedTermHighlightEnabled: boolean;
  setIsRejectedTermHighlightEnabled: (enabled: boolean) => void;
  isDictionaryTermHighlightEnabled: boolean;
  setIsDictionaryTermHighlightEnabled: (enabled: boolean) => void;
  /**
   * What the dictionary sidebar's Oppslag should show: a term (after clicking a marked word in the
   * text) or a search (from the bubble menu). A new object per request, so repeating one still applies.
   */
  dictionaryRequest: DictionaryRequest | null;
  openDictionaryTerm: (termId: string) => void;
  openDictionarySearch: (search: string) => void;
  openInternalLinksDialog: (selectedText?: string) => Promise<InternalLinksDialogResult | undefined>;
  openGuideLinkDialog: (selectedText?: string) => Promise<GuideLinkDialogResult | undefined>;
  openCitationLinkDialog: (selectedText?: string) => Promise<CitationLinkDialogResult | undefined>;
  openMigrateCitationsDialog: () => Promise<MigrateCitationsDialogResult | undefined>;
  openStandardLinkDialog: (selectedText?: string) => Promise<StandardLinkDialogResult | undefined>;
  snapshotState: SnapshotState;
  setSnapshotState: (state: SnapshotState) => void;
}

const GuideCmsContextInstance = React.createContext<GuideCmsContext | undefined>(undefined);

export const useGuideCmsContext = (): GuideCmsContext => {
  const context = React.useContext(GuideCmsContextInstance);
  if (!context) {
    throw new Error('useGuideCmsContext must be used within a GuideCmsContextProvider');
  }
  return context;
};

export const GuideCmsContextProvider: FC<{
  guideId: string;
  archivedTiptapDocument?: TiptapDocumentItemFragment;
  archivedVersion?: string;
  children: React.ReactNode;
}> = ({ guideId, archivedTiptapDocument, archivedVersion, children }) => {
  const [editor, setEditor] = useState<Editor | null>(null);
  const [provider, setProvider] = useState<TiptapCollabProvider>();
  const [sidebarType, setSidebarType] = useState<SidebarType>('toc');
  const [isValidSchema, setIsValidSchema] = useState(true);
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(false);
  const [isReferenceCandidateHighlightEnabled, setIsReferenceCandidateHighlightEnabled] = useState<boolean>(false);
  const [isRejectedTermHighlightEnabled, setIsRejectedTermHighlightEnabled] = useState<boolean>(false);
  const [isDictionaryTermHighlightEnabled, setIsDictionaryTermHighlightEnabled] = useState<boolean>(false);
  const [dictionaryRequest, setDictionaryRequest] = useState<DictionaryRequest | null>(null);
  const openDictionary = useCallback((request: DictionaryRequest) => {
    setDictionaryRequest(request);
    setSidebarType('dictionary');
    setIsSidebarOpen(true);
  }, []);
  const openDictionaryTerm = useCallback((termId: string) => openDictionary({ termId }), [openDictionary]);
  const openDictionarySearch = useCallback((search: string) => openDictionary({ search }), [openDictionary]);
  useDictionarySync(editor, openDictionaryTerm);
  const [_, setForceRefreshCount] = useState<number>(0);
  const [snapshotState, setSnapshotState] = useState<SnapshotState>({
    versions: [],
    currentVersion: 0,
    latestVersion: 0,
  });
  const { data: allGuidesData } = useQuery(GET_GUIDE_AND_LINKS, { variables: { id: guideId } });
  const { data: allStandardsData } = useQuery(ALL_STANDARDS);

  const { data: guideCmsInfoData, loading: isLoadingGuideCmsInfo } = useQuery(GUIDE_CMS_INFO, { variables: { id: guideId } });
  const [tiptapDocument, setTiptapDocument] = useState<TiptapDocumentItemFragment | null>(archivedTiptapDocument ?? null);
  const internalLinksModal = useModal<InternalLinksDialogProps, InternalLinksDialogResult>({ data: {} });
  const guideLinkModal = useModal<GuideLinkDialogProps, GuideLinkDialogResult>({ data: { guideId } });
  const citationLinkModal = useModal<CitationLinkDialogProps, CitationLinkDialogResult>({ data: {} });
  const migrateCitationsModal = useModal<MigrateCitationsDialogProps, MigrateCitationsDialogResult>({ data: {} });
  const standardLinkModal = useModal<StandardLinkDialogProps, StandardLinkDialogResult>({ data: {} });
  const isArchive = !!archivedTiptapDocument;

  let release: Release;
  const releases = guideCmsInfoData?.guide?.releases ?? [];
  if (isArchive) {
    release = releases.find(
      (r) =>
        r?.guide?.tiptapDocument?.documentName === archivedTiptapDocument.documentName &&
        r?.guide?.tiptapDocument?.documentVersion === archivedTiptapDocument.documentVersion,
    );
  } else {
    release = releases[releases.length - 1];
  }

  const openInternalLinksDialog = useCallback(
    async (selectedText?: string) => {
      const result = await internalLinksModal.open({
        selectedText,
      });
      return result;
    },
    [internalLinksModal, editor],
  );

  const openGuideLinkDialog = useCallback(
    async (selectedText?: string) => {
      const result = await guideLinkModal.open({
        selectedText,
        guideId,
      });
      return result;
    },
    [guideLinkModal, guideId, editor],
  );

  const openStandardLinkDialog = useCallback(
    async (selectedText?: string) => {
      const result = await standardLinkModal.open({
        selectedText,
      });
      return result;
    },
    [standardLinkModal, editor],
  );

  const openCitationLinkDialog = useCallback(
    async (selectedText?: string) => {
      const result = await citationLinkModal.open({
        selectedText,
      });
      return result;
    },
    [citationLinkModal, editor],
  );

  const openMigrateCitationsDialog = useCallback(
    async () => migrateCitationsModal.open({}),
    [migrateCitationsModal, editor],
  );

  const getGuideTitle = useCallback(
    (guideId: string) => {
      const guide = allGuidesData?.guides?.find((guide) => guide?.id === guideId);
      if (!guide) {
        return null;
      }
      return `${guide.docName} - ${guide.docTitle}`;
    },
    [allGuidesData],
  );

  const getStandardTitle = useCallback(
    (standardId: string) => {
      const standard = allStandardsData?.standards?.find((standard) => standard?.id === standardId);
      if (!standard) {
        return null;
      }
      return `${standard.name} - ${standard.title}`;
    },
    [allStandardsData],
  );

  const getCitationText = useCallback(
    (citationId: string) => {
      return guideCmsInfoData?.guide?.citations?.find((c) => c?.id === citationId)?.text ?? null;
    },
    [guideCmsInfoData],
  );

  const forceRefreshEditors = () => {
    setForceRefreshCount((prev) => prev + 1);
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  return (
    <GuideCmsContextInstance.Provider
      value={{
        isLoading: isLoadingGuideCmsInfo,
        isArchive,
        archivedVersion,
        guideId,
        guide: guideCmsInfoData?.guide,
        release,
        tiptapDocument: tiptapDocument ?? guideCmsInfoData?.guide?.tiptapDocument ?? null,
        setTiptapDocument,
        isValidSchema,
        setIsValidSchema,
        editor,
        provider,
        sidebarType,
        isSidebarOpen,
        toggleSidebar,
        setSidebarType,
        setEditor,
        setProvider,
        forceRefreshEditors,
        getGuideTitle,
        getStandardTitle,
        getCitationText,
        isReferenceCandidateHighlightEnabled,
        setIsReferenceCandidateHighlightEnabled,
        isRejectedTermHighlightEnabled,
        setIsRejectedTermHighlightEnabled,
        isDictionaryTermHighlightEnabled,
        setIsDictionaryTermHighlightEnabled,
        dictionaryRequest,
        openDictionaryTerm,
        openDictionarySearch,
        openInternalLinksDialog,
        openGuideLinkDialog,
        openCitationLinkDialog,
        openMigrateCitationsDialog,
        openStandardLinkDialog,
        snapshotState,
        setSnapshotState,
      }}>
      {internalLinksModal.isOpen && <InternalLinksDialog {...internalLinksModal} />}
      {guideLinkModal.isOpen && <GuideLinkDialog {...guideLinkModal} />}
      {citationLinkModal.isOpen && <CitationLinkDialog {...citationLinkModal} />}
      {migrateCitationsModal.isOpen && <MigrateCitationsDialog {...migrateCitationsModal} />}
      {standardLinkModal.isOpen && <StandardLinkDialog {...standardLinkModal} />}
      {children}
    </GuideCmsContextInstance.Provider>
  );
};
